import { useFeedingSchedule } from '@/hooks/useFeedingSchedule';
import { slotsForDoc } from '@/lib/feedingSchedule';
import { docOn } from '@/lib/crop';
import { FeedMix } from '@/components/entry/FeedMix';
import { DateBar } from '@/components/entry/DateBar';
import { draftBlend, type FeedMixDraft } from '@/lib/feedMix';
import { useState, useCallback, useEffect, useRef } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeft, Utensils, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useMembership } from '@/hooks/useMembership';
import { useDebouncedSignal } from '@/hooks/useDebouncedSignal';
import { useUnsavedEditsWarning } from '@/hooks/useUnsavedEditsWarning';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import {
  NumericInput,
  SelectInput,
  TextArea,
  AutoSaveIndicator,
  type SaveStatus,
} from '@/components/entry';
import { FeedCalculatorCard } from '@/components/entry/FeedCalculatorCard';
import { cn } from '@/lib/cn';
import { format, parse, subDays } from 'date-fns';
import { toNumber, toNumberOrNull } from '@/lib/number';
import { todayInPH, parsePhDate } from '@/lib/phDate';
import { qk } from '@/lib/queryKeys';
import { FEED_TYPE_OPTIONS } from '@/lib/feedTypes';
import { writeSignal } from '@/lib/writeSignal';
import { ApiError, registerAutosaveFlusher, saveFailureMessage } from '@/lib/apiErrors';
import { useWaitingForConnection } from '@/hooks/useWaitingForConnection';
import type { Feeding, FeedRecommendation } from '@/types/entry';
import type { ApiResponse, FarmResponse } from '@/types/api';
import type { FeedingCreate, PondDetailResponse } from '@/types/generated/openapi';
import { randomId } from '@/lib/randomId';

// Feeding has its own DOC-dependent slot schedule, independent of water-reading
// slots — do NOT couple it to ReadingSlot.
type FeedingSlot = FeedingCreate['feeding_slot'];
const ALL_FEEDING_SLOTS: FeedingSlot[] = ['7am', '10am', '11am', '1pm', '3pm', '4pm', '7pm'];
// 11am/3pm (#154) get a card only when the farm's feeding_slots setting lists
// them or the day already has a feeding there; otherwise they're offered as a
// compact "other time" button so 07/10/13/16/19h days carry no empty rows.
const DEFAULT_FEEDING_SLOTS: string[] = ['7am', '10am', '1pm', '4pm', '7pm'];

// A typed amount above this multiple of the pond's recent biggest single feeding
// (or above the fallback when there is no history) asks for a confirm before saving.
const UNUSUAL_AMOUNT_FACTOR = 3;
const UNUSUAL_AMOUNT_FALLBACK_KG = 100;
const HISTORY_DAYS = 14;
// The API rejects a feeding above 10,000 kg (feed_amount_kg le 10000).
const MAX_FEED_AMOUNT_KG = 10000;
// One hung request would otherwise hold up the whole write queue (see writeScope).
const WRITE_TIMEOUT_MS = 20_000;

function failureMessage(err: unknown): string {
  return saveFailureMessage(err, 'Unable to save feeding');
}

interface FeedingSearchParams {
  date?: string;
}

export const Route = createFileRoute('/_app/entry/pond/$pondId/feeding')({
  validateSearch: (search: Record<string, unknown>): FeedingSearchParams => {
    return {
      date: typeof search.date === 'string' ? search.date : undefined,
    };
  },
  component: FeedingForm,
});

// A second feed being set up on an unsaved slot rides along until the one create request (#361).
type SlotDraft = Partial<Feeding> & { mix?: FeedMixDraft | null };

function FeedingForm() {
  const { pondId } = Route.useParams();
  const { date } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // DELETE is supervisor+; technicians would only get a 403 (#169).
  const { membership, isLoading: membershipLoading, isSupervisorOrAbove: canDelete } = useMembership();

  const dateStr = date || todayInPH();
  const displayDate = parse(dateStr, 'yyyy-MM-dd', parsePhDate(dateStr));

  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [draftById, setDraftById] = useState<Record<string, Partial<Feeding>>>({});
  const pendingChangesRef = useRef<Record<string, Partial<Feeding>>>({});
  const [confirmAmount, setConfirmAmount] = useState<Record<string, number>>({});
  // Slots whose amount field currently holds a number that has not been committed yet.
  const [typingSlots, setTypingSlots] = useState<Record<string, boolean>>({});
  // Slots whose amount field is showing a refusal message.
  const [refusedSlots, setRefusedSlots] = useState<Record<string, boolean>>({});
  const amountRefs = useRef<Record<string, HTMLInputElement | null>>({});
  // Amounts typed in a field that has not blurred yet; leaving the page never fires blur.
  const typingRef = useRef<Record<string, number | null>>({});
  const notesTypingRef = useRef<Record<string, string>>({});
  const tabFromAmountRef = useRef<string | null>(null);
  const focusKeepRef = useRef<string | null>(null);
  const keepRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Slots opened by tapping "Add feeding" but not yet saved. Nothing is written
  // until an amount is entered: a feeding row of any amount marks the day's
  // feeding checklist complete.
  const [slotDraft, setSlotDraft] = useState<Record<string, SlotDraft>>({});
  // The amount as typed, before it is committed: the mix preview follows it.
  const [typedAmount, setTypedAmount] = useState<Record<string, number | null>>({});

  // Fetch pond data
  const { data: pond, isLoading: pondLoading } = useQuery({
    queryKey: qk.pond.detail(membership?.farmId, pondId),
    queryFn: async () => {
      const response = await api.get<ApiResponse<PondDetailResponse>>(
        `/api/v1/farms/${membership?.farmId}/ponds/${pondId}/detail`
      );
      return response.data;
    },
    enabled: !!pondId && !!membership?.farmId,
  });

  // Same key/shape as the settings page, so the two share one cache entry.
  const { data: farmData } = useQuery({
    queryKey: ['farm', membership?.farmId],
    queryFn: () => api.get<{ data: FarmResponse }>(`/api/v1/farms/${membership?.farmId}`),
    enabled: !!membership?.farmId,
    staleTime: 5 * 60 * 1000,
  });
  const { data: schedule, isLoading: scheduleLoading } = useFeedingSchedule(membership?.farmId);

  const cycleId = pond?.active_cycle?.id;
  const feedingsKey = ['feedings', cycleId, dateStr] as const;

  const resetKey = `${cycleId}-${dateStr}`;
  // A create's callbacks can run after the day has changed (the date bar keeps this page mounted). A failed save
  // goes back to the day it was typed for: parked here while another day is showing, put back when its day returns.
  const shownDayRef = useRef(resetKey);
  const parkedDraftsRef = useRef<Record<string, Record<string, SlotDraft>>>({});
  // The draft each create was sent from: the request carries the blend, not the mix draft that built it.
  const sentDraftRef = useRef<Record<string, SlotDraft | undefined>>({});
  useEffect(() => {
    shownDayRef.current = resetKey;
    setDraftById({});
    setSlotDraft(parkedDraftsRef.current[resetKey] ?? {});
    delete parkedDraftsRef.current[resetKey];
    setConfirmAmount({});
    setTypingSlots({});
    setTypedAmount({});
    pendingChangesRef.current = {};
  }, [resetKey]);

  // Fetch feedings for the day
  const { data: feedings, isLoading: feedingsLoading } = useQuery({
    queryKey: feedingsKey,
    queryFn: async () => {
      const response = await api.get<{ data: Feeding[] }>(
        `/api/v1/farms/${membership?.farmId}/feedings`,
        {
          params: {
            cycle_id: cycleId!,
            start_date: dateStr,
            end_date: dateStr,
          },
        }
      );
      return response.data || [];
    },
    enabled: !!cycleId && !!membership?.farmId,
  });

  // Prior days only: today's rows come from the live list above. 14 days x 7 slots stays
  // under the endpoint's page cap of 100.
  const { data: history } = useQuery({
    queryKey: ['feeding-history', cycleId, dateStr],
    queryFn: async () => {
      const response = await api.get<{ data: Feeding[] }>(
        `/api/v1/farms/${membership?.farmId}/feedings`,
        {
          params: {
            cycle_id: cycleId!,
            start_date: format(subDays(displayDate, HISTORY_DAYS), 'yyyy-MM-dd'),
            end_date: format(subDays(displayDate, 1), 'yyyy-MM-dd'),
            limit: '100',
          },
        }
      );
      return response.data || [];
    },
    enabled: !!cycleId && !!membership?.farmId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch feed calculator recommendation
  const { data: recommendation, isLoading: recLoading } = useQuery({
    queryKey: ['feed-recommendation', cycleId],
    queryFn: async () => {
      const response = await api.get<{ data: FeedRecommendation }>(
        `/api/v1/farms/${membership?.farmId}/cycles/${cycleId}/feed-recommendation`
      );
      return response.data;
    },
    enabled: !!cycleId && !!membership?.farmId,
  });

  // Calculate daily total
  const dailyTotal = feedings?.reduce((sum, f) => {
    const draft = draftById[f.id];
    const amount = draft?.feed_amount_kg ?? f.feed_amount_kg;
    return sum + toNumber(amount, 0);
  }, 0) || 0;
  const recommendedAmount = toNumber(recommendation?.daily_feed_kg, 0);
  const percentOfRecommended = recommendedAmount > 0 ? Math.round((dailyTotal / recommendedAmount) * 100) : 0;

  // Never a silent default: the type the guide recommends for this DOC (a real
  // recommendation naming a single code, not the hard-coded no-data fallback), else the
  // one this cycle used last, else the user has to choose.
  const isFeedType = (code: string | null | undefined): code is string =>
    !!code && FEED_TYPE_OPTIONS.some((o) => o.value === code);
  const recommendedFeedType =
    recommendedAmount > 0 && isFeedType(recommendation?.feed_type) ? recommendation.feed_type : undefined;
  const lastUsedFeedType = [...(history ?? []), ...(feedings ?? [])]
    .filter((f) => isFeedType(f.feed_type))
    .sort((a, b) =>
      `${a.feeding_date}|${a.created_at}`.localeCompare(`${b.feeding_date}|${b.created_at}`)
    )
    .at(-1)?.feed_type;
  const defaultFeedType = recommendedFeedType ?? lastUsedFeedType ?? undefined;

  const waitingForConnection = useWaitingForConnection();

  const farmId = membership?.farmId;
  const invalidateFeedingViews = () => {
    queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
    queryClient.invalidateQueries({ queryKey: ['feedings', farmId] });
    queryClient.invalidateQueries({ queryKey: qk.pond.entryStatus(pondId, dateStr) });
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'entry-checklist' && q.queryKey[2] === farmId,
    });
  };

  // One timer, so a create and an edit finishing close together don't cut each other's
  // "Saved" short.
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const showSaved = () => {
    setSaveStatus('saved');
    clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
  };

  // Create mutation
  const writeScope = { id: `feeding-writes-${cycleId}` };

  const createMutation = useMutation({
    scope: writeScope,
    // POST isn't idempotent: a retry after a lost response 409s on the row the
    // first attempt created, and the UI would drop it as failed.
    retry: false,
    mutationFn: async (data: Partial<Feeding>) => {
      return api.post(
        `/api/v1/farms/${membership?.farmId}/feedings`,
        {
          cycle_id: cycleId,
          feeding_date: dateStr,
          ...data,
        },
        { signal: writeSignal(WRITE_TIMEOUT_MS) }
      );
    },
    onMutate: async (data) => {
      setSaveStatus('saving');
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: feedingsKey });
      const previous = queryClient.getQueryData<Feeding[]>(feedingsKey);
      const nowIso = new Date().toISOString();
      const optimistic: Feeding = {
        id: `optimistic-${randomId()}`,
        cycle_id: cycleId,
        farm_id: membership?.farmId || '',
        feeding_date: dateStr,
        feeding_slot: (data as Feeding).feeding_slot || '7am',
        feed_amount_kg: (data as Feeding).feed_amount_kg ?? 0,
        feed_type: (data as Feeding).feed_type ?? null,
        blend: (data as Feeding).blend ?? null,
        feed_brand: (data as Feeding).feed_brand ?? null,
        feed_tray_check: (data as Feeding).feed_tray_check ?? null,
        notes: (data as Feeding).notes ?? null,
        data_source: 'manual',
        technician_id: null,
        created_at: nowIso,
        last_modified: nowIso,
      };
      const next = previous ? [...previous, optimistic] : [optimistic];
      queryClient.setQueryData(feedingsKey, next);
      return { previous, optimisticId: optimistic.id };
    },
    onError: (err, vars, context) => {
      setSaveStatus('error');
      const sentDay = `${vars.cycle_id}-${vars.feeding_date}`;
      const sentKey = ['feedings', vars.cycle_id, vars.feeding_date] as const;
      let edited: Partial<Feeding> | undefined;
      if (context?.optimisticId) {
        const optimisticId = context.optimisticId;
        queryClient.setQueryData(sentKey, (old: Feeding[] | undefined) =>
          old?.filter((f) => f.id !== optimisticId)
        );
        edited = pendingChangesRef.current[optimisticId];
        if (edited) {
          const rest = { ...pendingChangesRef.current };
          delete rest[optimisticId];
          pendingChangesRef.current = rest;
        }
      }
      const slot = vars.feeding_slot;
      const alreadyExists = err instanceof ApiError && err.status === 409;
      if (slot) {
        const draftKey = `${sentDay}|${slot}`;
        const sentFrom = sentDraftRef.current[draftKey];
        delete sentDraftRef.current[draftKey];
        const onShownDay = sentDay === shownDayRef.current;
        if (!onShownDay) {
          const others = { ...parkedDraftsRef.current[sentDay] };
          delete others[slot];
          parkedDraftsRef.current[sentDay] = alreadyExists ? others : { ...others, [slot]: { ...sentFrom, ...vars, ...edited } };
        } else {
          setSlotDraft((prev) => {
            if (alreadyExists) {
              const rest = { ...prev };
              delete rest[slot];
              return rest;
            }
            return { ...prev, [slot]: { ...prev[slot], ...vars, ...edited } };
          });
        }
      }
      if (alreadyExists) {
        queryClient.invalidateQueries({ queryKey: sentKey });
      }
      toast.error(failureMessage(err), {
        id: `feeding-create-error-${vars.feeding_slot}`,
      });
    },
    onSuccess: (result, vars, context) => {
      // An edit buffered behind this create sets its own saving/saved when it goes out.
      const editQueued = !!(context?.optimisticId && pendingChangesRef.current[context.optimisticId]);
      const created = (result as { data?: Feeding })?.data ?? (result as Feeding | undefined);
      const sentDay = `${vars.cycle_id}-${vars.feeding_date}`;
      const sentKey = ['feedings', vars.cycle_id, vars.feeding_date] as const;
      const current = queryClient.getQueryData<Feeding[]>(sentKey) || [];
      let next = current;
      if (context?.optimisticId) {
        next = current.filter((f) => f.id !== context.optimisticId);
      }
      if (created) {
        next = [...next, created];
      }
      queryClient.setQueryData(sentKey, next);
      if (vars.feeding_slot) {
        const slot = vars.feeding_slot;
        delete sentDraftRef.current[`${sentDay}|${slot}`];
        if (sentDay !== shownDayRef.current) {
          // Another day is showing: what is typed there is not this save's draft.
          if (parkedDraftsRef.current[sentDay]) delete parkedDraftsRef.current[sentDay][slot];
        } else {
          setSlotDraft((prev) => {
            if (!(slot in prev)) return prev;
            const rest = { ...prev };
            delete rest[slot];
            return rest;
          });
        }
      }
      invalidateFeedingViews();
      if (created && context?.optimisticId) {
        setDraftById((prev) => {
          if (!(context.optimisticId in prev)) return prev;
          const { [context.optimisticId]: draft, ...rest } = prev;
          return { ...rest, [created.id]: draft };
        });
        if (pendingChangesRef.current[context.optimisticId]) {
          const pending = pendingChangesRef.current[context.optimisticId];
          const nextPending = { ...pendingChangesRef.current };
          delete nextPending[context.optimisticId];
          nextPending[created.id] = pending;
          pendingChangesRef.current = nextPending;
          setSaveStatus('saving');
          scheduleSave();
        }
      }
      if (!editQueued) showSaved();
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    scope: writeScope,
    mutationFn: async ({ id, ...data }: { id: string } & Partial<Feeding>) => {
      return api.put(`/api/v1/farms/${membership?.farmId}/feedings/${id}`, data, {
        signal: writeSignal(WRITE_TIMEOUT_MS),
      });
    },
    onMutate: async ({ id, ...data }) => {
      setSaveStatus('saving');
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: feedingsKey });
      const previous = queryClient.getQueryData<Feeding[]>(feedingsKey);
      const previousRow = previous?.find((f) => f.id === id);
      if (previous) {
        const next = previous.map((feeding) =>
          feeding.id === id ? { ...feeding, ...data } : feeding
        );
        queryClient.setQueryData(feedingsKey, next);
      }
      return { previousRow };
    },
    onError: (err, vars, context) => {
      setSaveStatus('error');
      // Only revert the failed row, not the entire list
      if (context?.previousRow) {
        queryClient.setQueryData(feedingsKey, (old: Feeding[] | undefined) => {
          if (!old) return old;
          return old.map((item) =>
            item.id === vars.id ? context.previousRow! : item
          );
        });
      }
      // The reverted row shows the server value again; a draft would keep the unsaved one on screen.
      setDraftById((prev) => {
        const draft = prev[vars.id];
        if (!draft) return prev;
        const rest = { ...draft };
        (Object.keys(vars) as (keyof Feeding)[]).forEach((field) => {
          if (field !== 'id' && rest[field] === vars[field]) delete rest[field];
        });
        const next = { ...prev };
        if (Object.keys(rest).length) next[vars.id] = rest;
        else delete next[vars.id];
        return next;
      });
      // Issue #118: surface the failure with a toast (AutoSaveIndicator alone is easy to miss).
      toast.error(failureMessage(err), { id: `feeding-save-error-${vars.id}` });
    },
    onSuccess: (result) => {
      showSaved();
      const updated = (result as { data?: Feeding })?.data ?? (result as Feeding | undefined);
      if (updated) {
        const current = queryClient.getQueryData<Feeding[]>(feedingsKey) || [];
        const next = current.map((feeding) =>
          feeding.id === updated.id ? updated : feeding
        );
        queryClient.setQueryData(feedingsKey, next);
      }
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    scope: writeScope,
    mutationFn: async (id: string) => {
      return api.delete(`/api/v1/farms/${membership?.farmId}/feedings/${id}`, {
        signal: writeSignal(WRITE_TIMEOUT_MS),
      });
    },
    onMutate: async (id) => {
      if (!cycleId) return undefined;
      await queryClient.cancelQueries({ queryKey: feedingsKey });
      const previous = queryClient.getQueryData<Feeding[]>(feedingsKey);
      const deletedRow = previous?.find((f) => f.id === id);
      if (previous) {
        queryClient.setQueryData(feedingsKey, previous.filter((f) => f.id !== id));
      }
      setDraftById((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (pendingChangesRef.current[id]) {
        const next = { ...pendingChangesRef.current };
        delete next[id];
        pendingChangesRef.current = next;
      }
      return { deletedRow };
    },
    onError: (err, _vars, context) => {
      // Put back only the row that failed to delete: other writes may have changed the
      // list since the snapshot, and a whole-list restore would resurrect or hide rows.
      const gone = err instanceof ApiError && err.status === 404;
      const deletedRow = context?.deletedRow;
      if (deletedRow && !gone) {
        queryClient.setQueryData(feedingsKey, (old: Feeding[] | undefined) =>
          old && !old.some((f) => f.id === deletedRow.id) ? [...old, deletedRow] : old
        );
      }
      queryClient.invalidateQueries({ queryKey: feedingsKey });
      toast.error(failureMessage(err), { id: 'feeding-delete-error' });
    },
    onSuccess: () => {
      invalidateFeedingViews();
    },
  });

  const flushPending = useCallback(() => {
    const pending = pendingChangesRef.current;
    const ids = Object.keys(pending);
    if (!ids.length) return;

    const remaining: Record<string, Partial<Feeding>> = {};
    ids.forEach((id) => {
      if (id.startsWith('optimistic-')) {
        remaining[id] = pending[id];
        return;
      }
      updateMutation.mutate({ id, ...pending[id] });
    });

    pendingChangesRef.current = remaining;
  }, [updateMutation]);

  const { signal: scheduleSave, flush: flushNow } = useDebouncedSignal(flushPending, { delayMs: 600 });


  // Issue #86: wire to the 401 flush registry.
  useEffect(() => {
    return registerAutosaveFlusher(() => {
      flushNow();
      flushPending();
    });
  }, [flushNow, flushPending]);

  const queueFieldSave = useCallback(
    (feeding: Feeding, field: keyof Feeding, value: unknown) => {
      const feedingId = feeding.id;
      const currentValue = feeding[field];

      setDraftById((prev) => ({
        ...prev,
        [feedingId]: {
          ...prev[feedingId],
          [field]: value as Feeding[keyof Feeding],
        },
      }));

      // The API returns amounts as strings ("10.000") and empty notes as null.
      const unchanged =
        field === 'feed_amount_kg'
          ? toNumberOrNull(value as number | null) === toNumberOrNull(currentValue as number | string | null)
          : (value ?? '') === (currentValue ?? '');
      if (unchanged) {
        const existing = pendingChangesRef.current[feedingId];
        if (existing && field in existing) {
          const next = { ...pendingChangesRef.current };
          const nextFields = { ...existing };
          delete nextFields[field];
          if (Object.keys(nextFields).length) {
            next[feedingId] = nextFields;
          } else {
            delete next[feedingId];
          }
          pendingChangesRef.current = next;
        }
        return;
      }

      pendingChangesRef.current = {
        ...pendingChangesRef.current,
        [feedingId]: {
          ...pendingChangesRef.current[feedingId],
          [field]: value as Feeding[keyof Feeding],
        },
      };
      setSaveStatus('saving');
      scheduleSave();
    },
    [scheduleSave]
  );

  const openSlot = (slot: FeedingSlot) => {
    setSlotDraft((prev) => (slot in prev ? prev : { ...prev, [slot]: {} }));
  };

  const patchSlotDraft = (slot: string, patch: SlotDraft) => {
    setSlotDraft((prev) => ({ ...prev, [slot]: { ...prev[slot], ...patch } }));
  };

  const isUnusualAmount = (amount: number, editedId?: string) => {
    const recentMax = Math.max(
      0,
      ...(history ?? []).map((f) => toNumber(f.feed_amount_kg, 0)),
      ...(feedings ?? []).filter((f) => f.id !== editedId).map((f) => toNumber(f.feed_amount_kg, 0))
    );
    const limit = recentMax > 0 ? recentMax * UNUSUAL_AMOUNT_FACTOR : UNUSUAL_AMOUNT_FALLBACK_KG;
    // Cents: 3 x 4.3 is 12.899999999999999 in floats, which would flag an exact 12.9.
    return Math.round(amount * 100) > Math.round(limit * 100);
  };

  const clearConfirm = (slot: string) => {
    setConfirmAmount((prev) => {
      if (!(slot in prev)) return prev;
      const rest = { ...prev };
      delete rest[slot];
      return rest;
    });
  };

  const commitAmount = (
    slot: FeedingSlot,
    feeding: Feeding | undefined,
    value: number | null,
    notes?: string
  ) => {
    if (feeding) {
      queueFieldSave(feeding, 'feed_amount_kg', value);
      flushNow();
    } else if (value === null) {
      patchSlotDraft(slot, { feed_amount_kg: undefined });
    } else {
      patchSlotDraft(slot, { feed_amount_kg: value });
      createFeeding(slot, value, notes);
    }
  };

  // Returns true when the amount was held back behind the confirm instead of saved.
  const handleAmountBlur = (
    slot: FeedingSlot,
    feeding: Feeding | undefined,
    value: number | null,
    notes?: string
  ): boolean => {
    delete typingRef.current[slot];
    setTypingSlots((prev) => (slot in prev ? { ...prev, [slot]: false } : prev));
    setTypedAmount((prev) => {
      if (!(slot in prev)) return prev;
      const rest = { ...prev };
      delete rest[slot];
      return rest;
    });
    const current = feeding
      ? toNumberOrNull(draftById[feeding.id]?.feed_amount_kg ?? feeding.feed_amount_kg)
      : null;
    if (value !== null && value !== current && isUnusualAmount(value, feeding?.id)) {
      setConfirmAmount((prev) => ({ ...prev, [slot]: value }));
      // Tab was chosen before this render, so focus already went past the banner.
      if (tabFromAmountRef.current === slot) focusKeepRef.current = slot;
      tabFromAmountRef.current = null;
      return true;
    }
    tabFromAmountRef.current = null;
    clearConfirm(slot);
    commitAmount(slot, feeding, value, notes);
    return false;
  };

  const keepAmount = (slot: FeedingSlot, feeding: Feeding | undefined) => {
    const amount = confirmAmount[slot];
    if (amount === undefined) return;
    clearConfirm(slot);
    commitAmount(slot, feeding, amount);
    // The button that had focus unmounts with the banner.
    amountRefs.current[slot]?.focus();
  };

  const changeAmount = (slot: FeedingSlot) => {
    const input = amountRefs.current[slot];
    input?.focus();
    input?.select();
  };

  // Nothing is written until a feed type is known (see defaultFeedType).
  // A second feed that isn't finished holds the save too, so a mix someone set up is never saved as a plain feed.
  const createFeeding = (
    slot: FeedingSlot,
    amount: number,
    typedNotes?: string,
    typeOverride?: string,
    mixOverride?: FeedMixDraft | null
  ) => {
    if (!cycleId) return;
    const values = slotDraft[slot];
    const feedType = typeOverride ?? values?.feed_type ?? defaultFeedType;
    if (!feedType) return;
    const { blend, error: mixError } = draftBlend(feedType, mixOverride !== undefined ? mixOverride : values?.mix);
    if (mixError) return;
    const notes = typedNotes ?? values?.notes;
    sentDraftRef.current[`${cycleId}-${dateStr}|${slot}`] = values;
    createMutation.mutate({
      // Pinned now: a create sent as the page is left (day or pond changed) runs with the NEW page's values.
      cycle_id: cycleId,
      feeding_date: dateStr,
      feeding_slot: slot,
      feed_amount_kg: amount,
      // The main feed, not the largest component: a failed create restores this request into the draft,
      // and the server sets feed_type from the blend itself.
      feed_type: feedType,
      ...(blend ? { blend } : {}),
      ...(notes ? { notes } : {}),
    });
  };

  // The debounce is cancelled on unmount and the reset effect wipes the buffer on a
  // date/cycle change, so an edit made <600 ms before leaving would be dropped, and a
  // focused field never blurs on unmount. Effect cleanup runs before the next render's
  // effects, so this is the last committed closure (old query key, old rows). An unusual
  // amount is never saved on leave; the user is told instead.
  const leaveRef = useRef<() => void>(() => {});
  useEffect(() => {
    leaveRef.current = () => {
      const slotExists = (slot: string) =>
        !!feedings?.some((f) => f.feeding_slot === slot) || slot in slotDraft;
      // Slots whose unconfirmed amount would be dropped. A confirm that was then
      // replaced by a typed value is resolved below, not counted.
      const held = new Set(Object.keys(confirmAmount).filter(slotExists));
      let typeHeld = false;
      let mixHeld = false;
      const mixUnfinished = (slot: string) =>
        !!draftBlend(slotDraft[slot]?.feed_type ?? defaultFeedType ?? null, slotDraft[slot]?.mix).error;
      const slots = new Set([...Object.keys(typingRef.current), ...Object.keys(notesTypingRef.current)]);
      slots.forEach((slot) => {
        const feeding = feedings?.find((f) => f.feeding_slot === slot);
        // Deleted, or never opened: nothing to write back.
        if (!feeding && !(slot in slotDraft)) return;
        const amount = typingRef.current[slot];
        const notes = notesTypingRef.current[slot];
        if (feeding && notes !== undefined) queueFieldSave(feeding, 'notes', notes);
        if (amount !== null && amount !== undefined) {
          if (!feeding && !(slotDraft[slot]?.feed_type ?? defaultFeedType)) typeHeld = true;
          else if (!feeding && mixUnfinished(slot)) mixHeld = true;
          held.delete(slot);
          if (handleAmountBlur(slot as FeedingSlot, feeding, amount, notes)) held.add(slot);
        }
      });
      // A committed amount held by a mix that has become valid since (a percent typed without leaving
      // its box never settles by itself): leaving is the last chance to save it.
      const typedSlots = new Set(Object.keys(typingRef.current).filter((slot) => typingRef.current[slot] != null));
      Object.entries(slotDraft).forEach(([slot, d]) => {
        if (
          d.feed_amount_kg === undefined ||
          d.feed_amount_kg === null ||
          !d.mix ||
          typedSlots.has(slot) ||
          slot in confirmAmount ||
          feedings?.some((f) => f.feeding_slot === slot)
        ) {
          return;
        }
        createFeeding(slot as FeedingSlot, toNumber(d.feed_amount_kg, 0), notesTypingRef.current[slot]);
      });
      typingRef.current = {};
      notesTypingRef.current = {};
      flushPending();
      const missingType = typeHeld || Object.entries(slotDraft).some(
        ([slot, d]) =>
          d.feed_amount_kg !== undefined &&
          d.feed_amount_kg !== null &&
          !d.feed_type &&
          !defaultFeedType &&
          !feedings?.some((f) => f.feeding_slot === slot)
      );
      if (missingType) {
        toast.error('Not saved: choose a feed type first', { id: 'feeding-no-type' });
      }
      const mixUnsaved =
        mixHeld ||
        Object.entries(slotDraft).some(
          ([slot, d]) =>
            d.feed_amount_kg !== undefined &&
            d.feed_amount_kg !== null &&
            !feedings?.some((f) => f.feeding_slot === slot) &&
            mixUnfinished(slot)
        );
      if (mixUnsaved && !missingType) {
        toast.error('Not saved: finish the second feed first', { id: 'feeding-no-mix' });
      }
      if (held.size > 0) {
        toast.error('Not saved: confirm the amount first', { id: 'feeding-unconfirmed' });
      }
    };
  });
  useEffect(() => () => leaveRef.current(), [resetKey]);

  // Reload / tab close: an amount waiting on the soft confirm, typed and not yet committed, or
  // committed on a slot that is still held (no feed type, or a second feed not finished), is
  // unsaved too.
  useUnsavedEditsWarning(
    () =>
      Object.keys(confirmAmount).length > 0 ||
      Object.entries(slotDraft).some(
        ([slot, d]) =>
          d.feed_amount_kg !== undefined &&
          d.feed_amount_kg !== null &&
          !feedings?.some((f) => f.feeding_slot === slot)
      ) ||
      Object.values(typingRef.current).some((v) => v !== null) ||
      Object.keys(notesTypingRef.current).length > 0 ||
      Object.keys(pendingChangesRef.current).length > 0
  );

  // The page's own Back controls don't drop an amount that is waiting on the confirm
  // without saying so: the first tap keeps the page, brings the banner into view and
  // explains; a second tap within a few seconds leaves (the leave toast then repeats that
  // it was not saved).
  const backArmedAtRef = useRef(0);
  const allowBack = (e?: { preventDefault: () => void }) => {
    const slot = Object.keys(confirmAmount)[0];
    if (!slot || Date.now() - backArmedAtRef.current < 6000) return true;
    backArmedAtRef.current = Date.now();
    e?.preventDefault();
    toast.error(
      'This amount is not saved yet. Confirm or change it, or tap Back again to leave without saving.',
      { id: 'feeding-confirm-first', duration: 8000 }
    );
    const keep = keepRefs.current[slot];
    keep?.scrollIntoView({ block: 'center' });
    keep?.focus();
    return false;
  };

  // Switching apps or swiping the page away never unmounts the route. Only edits already
  // queued are sent: a number still being typed may be partial ("1" of "12.5") and a
  // wrong amount on a farm record is worse than an unsaved one.
  useEffect(() => {
    const flushQueuedOnHide = () => {
      if (document.visibilityState === 'hidden') flushNow();
    };
    document.addEventListener('visibilitychange', flushQueuedOnHide);
    window.addEventListener('pagehide', flushNow);
    return () => {
      document.removeEventListener('visibilitychange', flushQueuedOnHide);
      window.removeEventListener('pagehide', flushNow);
    };
  }, [flushNow]);

  // A confirm whose row or draft has vanished (refetch, delete elsewhere) has nothing left to confirm.
  useEffect(() => {
    if (!feedings) return;
    setConfirmAmount((prev) => {
      const stale = Object.keys(prev).filter(
        (slot) => !feedings.some((f) => f.feeding_slot === slot) && !(slot in slotDraft)
      );
      if (!stale.length) return prev;
      const next = { ...prev };
      stale.forEach((slot) => delete next[slot]);
      return next;
    });
  }, [feedings, slotDraft]);

  useEffect(() => {
    const slot = focusKeepRef.current;
    if (slot && keepRefs.current[slot]) {
      focusKeepRef.current = null;
      keepRefs.current[slot]?.focus();
    }
  });

  if (membershipLoading || pondLoading || feedingsLoading || scheduleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size={32} />
      </div>
    );
  }


  // Group feedings by slot
  const feedingsBySlot: Record<string, Feeding | undefined> = {};
  feedings?.forEach((f) => {
    feedingsBySlot[f.feeding_slot] = f;
  });
  // Slots that apply on this page's DOC (the farm's one list unless it opted in to a schedule by DOC).
  const dayDoc = pond?.active_cycle ? docOn(pond.active_cycle, dateStr) : null;
  const farmSlots = slotsForDoc(schedule, dayDoc) ?? farmData?.data?.settings?.feeding_slots ?? DEFAULT_FEEDING_SLOTS;
  const visibleSlots = ALL_FEEDING_SLOTS.filter(
    (slot) => farmSlots.includes(slot) || feedingsBySlot[slot] || slot in slotDraft
  );
  const otherSlots = ALL_FEEDING_SLOTS.filter((slot) => !visibleSlots.includes(slot));

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header: stays in view while the slots scroll, below the phone top bar */}
      <div className="sticky top-0 z-30 -my-2 py-2 bg-gray-50 flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => {
              if (allowBack()) navigate({ to: `/entry/pond/${pondId}`, search: { date: dateStr } });
            }}
            aria-label="Back"
            className="flex min-h-[44px] min-w-[44px] items-center justify-center -ml-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-50">
                <Utensils className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{pond?.name}</h1>
                <p className="text-sm text-gray-500">
                  Feeding • {format(displayDate, 'EEEE, MMMM d')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {waitingForConnection ? (
            <span role="status" className="text-sm font-medium text-amber-800 text-right">
              No connection, will save when back online
            </span>
          ) : (
            <>
              {saveStatus === 'idle' && Object.keys(confirmAmount).length === 0 && (
                <span className="text-sm text-gray-500">Saves automatically</span>
              )}
              <AutoSaveIndicator
                status={saveStatus}
                onRetry={() => updateMutation.reset()}
              />
            </>
          )}
        </div>
      </div>

      {/* Any past day can be back-filled; it is marked Late afterwards (#366) */}
      <DateBar
        className="mt-4"
        date={dateStr}
        today={todayInPH()}
        minDate={pond?.active_cycle?.stocking_date ?? null}
        onChange={(d) => {
          if (!allowBack()) return;
          navigate({ to: '/entry/pond/$pondId/feeding', params: { pondId }, search: { date: d === todayInPH() ? undefined : d } });
        }}
      />

      {/* Feed Calculator Recommendation */}
      <FeedCalculatorCard recommendation={recommendation} isLoading={recLoading} />

      {/* Daily Total Summary */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Daily Total</p>
            <p className="text-3xl font-bold text-gray-900 tabular-nums">
              {dailyTotal.toFixed(2)} <span className="text-lg font-normal text-gray-400">kg</span>
            </p>
          </div>
          {recommendedAmount > 0 && (
            <div className="text-right">
              <p className="text-sm text-gray-500">vs Recommended</p>
              <p className={cn(
                'text-2xl font-bold tabular-nums',
                percentOfRecommended >= 90 && percentOfRecommended <= 110
                  ? 'text-emerald-600'
                  : percentOfRecommended < 90
                    ? 'text-amber-600'
                    : 'text-red-600'
              )}>
                {percentOfRecommended}%
              </p>
            </div>
          )}
        </div>

        {/* Progress bar */}
        {recommendedAmount > 0 && (
          <div className="mt-4">
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  percentOfRecommended >= 90 && percentOfRecommended <= 110
                    ? 'bg-emerald-500'
                    : percentOfRecommended < 90
                      ? 'bg-amber-500'
                      : 'bg-red-500'
                )}
                style={{ width: `${Math.min(percentOfRecommended, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Feeding Slots */}
      <div className="space-y-4">
        <h2 className="font-semibold text-gray-900">Feeding Schedule</h2>

        {!cycleId && (
          <p className="text-sm text-gray-500">
            This pond has no active crop, so feeding can't be logged.
          </p>
        )}

        {cycleId && visibleSlots.map((slot) => {
          const feeding = feedingsBySlot[slot];
          const draft = feeding ? draftById[feeding.id] : undefined;
          const unsaved = !feeding ? slotDraft[slot] : undefined;
          const isOpen = !!feeding || !!unsaved;
          const unusualAmount = confirmAmount[slot];
          const savedAmount = feeding
            ? toNumberOrNull(draft?.feed_amount_kg ?? feeding.feed_amount_kg)
            : null;
          const draftAmount = unsaved?.feed_amount_kg;
          const needsType =
            !!unsaved &&
            unusualAmount === undefined &&
            draftAmount !== undefined &&
            draftAmount !== null &&
            !unsaved.feed_type &&
            !defaultFeedType;
          // One status line per slot: the confirm banner and the missing-type line carry
          // their own "Not saved yet".
          const mainType = unsaved ? (unsaved.feed_type ?? defaultFeedType ?? null) : null;
          const mix = unsaved ? draftBlend(mainType, unsaved.mix) : null;
          const needsMix =
            !!unsaved &&
            unusualAmount === undefined &&
            !needsType &&
            draftAmount !== undefined &&
            draftAmount !== null &&
            !!mix?.error;
          const mixNote = mix?.blend ? ' The mix is saved with it.' : '';
          const hint =
            unsaved && unusualAmount === undefined && !needsType && !needsMix
              ? refusedSlots[slot]
                ? 'Not saved yet.'
                : typingSlots[slot]
                  ? `Not saved yet. Tap outside the box to save.${mixNote}`
                  : `Not saved yet. Enter the amount to save.${mixNote}`
              : null;
          const describedBy =
            [
              hint && `feeding-hint-${slot}`,
              unusualAmount !== undefined && `feeding-confirm-${slot}`,
              needsType && `feeding-type-${slot}`,
              needsMix && `feeding-mix-${slot}`,
            ]
              .filter(Boolean)
              .join(' ') || undefined;

          return (
            <div
              key={slot}
              className={cn(
                'bg-white rounded-xl border shadow-sm overflow-hidden transition-all',
                feeding ? 'border-gray-200' : 'border-dashed border-gray-300'
              )}
            >
              <div className="px-5 py-4 min-h-[64px] bg-gray-50/50 border-b border-gray-100 flex items-center justify-between gap-3">
                <h3 className="font-medium text-gray-900">{slot}</h3>
                {hint && (
                  <span id={`feeding-hint-${slot}`} role="status" className="text-xs text-gray-500 text-right">
                    {hint}
                  </span>
                )}
                {feeding && canDelete && !feeding.id.startsWith('optimistic-') && (
                  <button
                    // Keeps the amount field focused so its blur doesn't queue a PUT for a row
                    // that is about to be deleted.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      delete typingRef.current[slot];
                      delete notesTypingRef.current[slot];
                      setSlotDraft((prev) => {
                        const next = { ...prev };
                        delete next[slot];
                        return next;
                      });
                      clearConfirm(slot);
                      deleteMutation.mutate(feeding.id);
                    }}
                    className="-my-2 -mr-2 flex h-11 w-11 items-center justify-center rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                    title="Remove"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {isOpen ? (
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <NumericInput
                        ref={(el) => {
                          amountRefs.current[slot] = el;
                        }}
                        label="Amount"
                        unit="kg"
                        value={
                          unusualAmount ??
                          toNumberOrNull(
                            feeding ? (draft?.feed_amount_kg ?? feeding.feed_amount_kg) : unsaved?.feed_amount_kg
                          )
                        }
                        min={0}
                        step={0.1}
                        placeholder={feeding ? undefined : 'Enter kg (0 if none)'}
                        aria-describedby={describedBy}
                        onValueChange={(value) => {
                          typingRef.current[slot] = value;
                          setTypedAmount((prev) => (prev[slot] === value ? prev : { ...prev, [slot]: value }));
                          setTypingSlots((prev) =>
                            prev[slot] === (value !== null) ? prev : { ...prev, [slot]: value !== null }
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Tab' && !e.shiftKey) tabFromAmountRef.current = slot;
                        }}
                        // A refused value never blurs through the handler, so a Tab pressed
                        // before it must not steer a later, unrelated blur to the Keep button.
                        onFocus={() => {
                          tabFromAmountRef.current = null;
                        }}
                        validate={(n) =>
                          n > MAX_FEED_AMOUNT_KG
                            ? `Amount can't be more than ${MAX_FEED_AMOUNT_KG.toLocaleString()} kg`
                            : null
                        }
                        onRefusalChange={(refused) =>
                          setRefusedSlots((prev) => (prev[slot] === refused ? prev : { ...prev, [slot]: refused }))
                        }
                        onBlur={(value) => handleAmountBlur(slot, feeding, value)}
                      />
                      {unusualAmount !== undefined && (
                        <div
                          id={`feeding-confirm-${slot}`}
                          role="alert"
                          className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3"
                        >
                          <p className="text-sm font-medium text-amber-900">
                            That's much more than usual. Check the amount.
                          </p>
                          <p className="mt-0.5 text-xs text-amber-800">
                            {savedAmount !== null ? `Still saved as ${savedAmount} kg.` : 'Not saved yet.'}
                          </p>
                          <div className="mt-3 flex gap-2">
                            <button
                              type="button"
                              ref={(el) => {
                                keepRefs.current[slot] = el;
                              }}
                              onClick={() => keepAmount(slot, feeding)}
                              className="min-h-[44px] flex-1 rounded-lg bg-amber-700 px-3 text-sm font-semibold text-white hover:bg-amber-800 transition-colors"
                            >
                              Keep {unusualAmount} kg
                            </button>
                            <button
                              type="button"
                              aria-label="Change amount"
                              onClick={() => changeAmount(slot)}
                              className="min-h-[44px] flex-1 rounded-lg border border-amber-300 bg-white px-3 text-sm font-semibold text-amber-900 hover:bg-amber-100 transition-colors"
                            >
                              Change
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      {/* A mixed feeding shows its mix instead of one feed type (#361). */}
                      {feeding && (draft?.blend ?? feeding.blend)?.length ? null : (
                      <SelectInput
                        label="Feed Type"
                        placeholder="Choose a feed type"
                        value={
                          (feeding
                            ? (draft?.feed_type ?? feeding.feed_type)
                            : (unsaved?.feed_type ?? defaultFeedType)) || ''
                        }
                        options={FEED_TYPE_OPTIONS}
                        aria-describedby={needsType ? `feeding-type-${slot}` : undefined}
                        onChange={(value) => {
                          if (feeding) {
                            queueFieldSave(feeding, 'feed_type', value);
                            return;
                          }
                          // A second feed equal to the new main feed is dropped, not kept as a clash.
                          const mixAfter =
                            unsaved?.mix && unsaved.mix.feed_type === value ? { ...unsaved.mix, feed_type: '' } : unsaved?.mix;
                          patchSlotDraft(slot, { feed_type: value, mix: mixAfter });
                          if (draftAmount !== undefined && draftAmount !== null) {
                            createFeeding(slot, toNumber(draftAmount, 0), undefined, value, mixAfter);
                          }
                        }}
                      />
                      )}
                      {unsaved && (
                        <FeedMix
                          mainType={mainType}
                          blend={null}
                          totalKg={slot in typedAmount ? typedAmount[slot] : (draftAmount ?? null)}
                          draft={{
                            value: unsaved.mix ?? null,
                            onChange: (value) => {
                              patchSlotDraft(slot, { mix: value });
                              // Dropping an unfinished second feed releases the save it was holding.
                              if (value === null && draftAmount !== undefined && draftAmount !== null) {
                                createFeeding(slot, toNumber(draftAmount, 0), undefined, undefined, null);
                              }
                            },
                            onSettled: (value) => {
                              if (draftAmount !== undefined && draftAmount !== null) {
                                createFeeding(slot, toNumber(draftAmount, 0), undefined, undefined, value);
                              }
                            },
                          }}
                        />
                      )}
                      {feeding && (
                        <FeedMix
                          mainType={(draft?.feed_type ?? feeding.feed_type) || null}
                          blend={draft && 'blend' in draft ? draft.blend : feeding.blend}
                          totalKg={draft?.feed_amount_kg ?? feeding.feed_amount_kg}
                          disabled={saveStatus === 'saving'}
                          onChange={(blend) => {
                            queueFieldSave(feeding, 'blend', blend);
                            // The server sets feed_type to the largest component: keep the local copy in step.
                            if (blend) queueFieldSave(feeding, 'feed_type', blend[0].feed_type);
                            flushNow();
                          }}
                        />
                      )}
                      {needsMix && (
                        <p id={`feeding-mix-${slot}`} role="status" className="mt-1.5 text-xs text-amber-700">
                          Not saved yet. Finish the second feed, or remove it.
                        </p>
                      )}
                      {needsType && (
                        <p id={`feeding-type-${slot}`} role="status" className="mt-1.5 text-xs text-amber-700">
                          Not saved yet. Choose a feed type to save.
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-4">
                    <TextArea
                      label="Notes"
                      value={(feeding ? (draft?.notes ?? feeding.notes) : unsaved?.notes) || ''}
                      onValueChange={(value) => {
                        notesTypingRef.current[slot] = value;
                      }}
                      onBlur={(value) => {
                        delete notesTypingRef.current[slot];
                        if (feeding) {
                          queueFieldSave(feeding, 'notes', value);
                          flushNow();
                        } else {
                          patchSlotDraft(slot, { notes: value });
                        }
                      }}
                      rows={2}
                      placeholder="Any observations about feeding..."
                    />
                  </div>
                </div>
              ) : (
                <div className="p-5">
                  <button
                    onClick={() => openSlot(slot)}
                    className="w-full py-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add feeding for {slot}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {cycleId && otherSlots.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <span>Other feeding time:</span>
            {otherSlots.map((slot) => (
              <button
                key={slot}
                onClick={() => openSlot(slot)}
                className="min-h-[44px] px-3 py-2 rounded-lg border border-dashed border-gray-300 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                {slot}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <Link
          to="/entry/pond/$pondId"
          params={{ pondId }}
          search={{ date: dateStr }}
          onClick={allowBack}
          className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          ← Back to entry hub
        </Link>
      </div>
    </div>
  );
}
